/**
 * SEO Utility Functions
 *
 * Helper functions for canonical URLs and SEO configuration retrieval.
 */

import type { PageSEOMetadata } from '../types/seo'
import { seoConfig } from '../data/seo-config'

const PRODUCTION_URL = 'https://ncesp.org'

/**
 * Get canonical URL for a page path
 * Always returns absolute production URL regardless of environment
 *
 * @param path - Page path (e.g., "/about", "/issues/child-support-reform")
 * @returns Absolute production URL
 */
export function getCanonicalUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  // Remove trailing slash except for homepage
  const cleanPath =
    normalizedPath === '/' ? '/' : normalizedPath.replace(/\/$/, '')

  return `${PRODUCTION_URL}${cleanPath}`
}

/**
 * Get SEO configuration for a specific page path
 *
 * @param path - Page path (e.g., "/about", "/leadership")
 * @returns SEO metadata or undefined if not found
 */
export function getSEOConfig(path: string): PageSEOMetadata | undefined {
  return seoConfig[path]
}

/**
 * Get absolute URL for an asset
 *
 * @param assetPath - Asset path (e.g., "/images/og/default.jpg")
 * @returns Absolute production URL
 */
export function getAbsoluteAssetUrl(assetPath: string): string {
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`
  return `${PRODUCTION_URL}${normalizedPath}`
}
